/**
 * URL scheme — canonical entity references.
 *
 * The ten URL shapes below are an INVARIANT from `functionality.md §3.1`
 * and `cook-as-process-design.md §8` — they can be added to but not
 * broken. Any rename or removal of a shape is a breaking change to every
 * client that bookmarked a URL.
 *
 * Shape table (spec authority: functionality.md §3.1, lines 178-207):
 *
 *   kind                | URL shape                              | Spec line
 *   --------------------+----------------------------------------+----------
 *   recipe              | /r/<recipe-uuid>                       | 179
 *   recipeBySlug        | /u/<handle>/<recipe-slug>              | 181
 *   attempt             | /r/<recipe-uuid>/a/<attempt-uuid>      | 184
 *   branch              | /r/<recipe-uuid>/b/<branch-uuid>       | 187
 *   recipeBatch         | /r/<recipe-uuid>/batch/<batch-uuid>    | 191
 *   batch               | /batch/<batch-uuid>                    | 196
 *   artefact            | /a/<artefact-uuid>                     | 200
 *   artefactBySlug      | /u/<handle>/a/<artefact-slug>          | 204
 *   collection          | /c/<collection-uuid>                   | 206
 *   collectionBySlug    | /u/<handle>/c/<slug>                   | 206
 *
 * The `/r/<recipe-uuid>/#<block-uuid>` fragment form in the spec is
 * intentionally NOT an entity ref — it is a client-side deep link inside
 * an already-rendered recipe. Fragment handling lives in the SPA router,
 * not here. `parseUrl` strips fragments before matching so `/r/<uuid>#b-…`
 * still resolves to a `recipe` ref.
 *
 * The `/a/<uuid>` namespace is shared by *attempt* (under `/r/.../a/`)
 * and *artefact* (top-level `/a/`). Disambiguation is by path prefix —
 * see functionality.md §3.1 lines 208-211. The bare `/a/<uuid>` form is
 * an ARTEFACT. This resolver follows the master plan exactly; callers
 * who want "attempt by UUID" must use the recipe-qualified
 * `/r/<uuid>/a/<uuid>` form.
 */

/** A canonical UUID: 8-4-4-4-12 lowercase hex. Shape-only validation. */
export type Uuid = string;

/** A URL-safe handle or slug: lowercase alphanum + hyphen, 1-64 chars,
 *  starts with an alphanumeric character. Conservative pattern — the
 *  spec leaves handle/slug rules unpinned beyond "rename-safe" and
 *  "independently editable" (functionality.md §3.1). If/when auth picks
 *  a stricter handle rule, tighten here; the URL resolver is the
 *  narrowest current enforcer. */
export type Handle = string;
export type Slug = string;

export type RecipeRef = {
  readonly kind: "recipe";
  readonly recipeUuid: Uuid;
};

export type RecipeBySlugRef = {
  readonly kind: "recipeBySlug";
  readonly handle: Handle;
  readonly slug: Slug;
};

export type AttemptRef = {
  readonly kind: "attempt";
  readonly recipeUuid: Uuid;
  readonly attemptUuid: Uuid;
};

export type BranchRef = {
  readonly kind: "branch";
  readonly recipeUuid: Uuid;
  readonly branchUuid: Uuid;
};

export type RecipeBatchRef = {
  readonly kind: "recipeBatch";
  readonly recipeUuid: Uuid;
  readonly batchUuid: Uuid;
};

export type BatchRef = {
  readonly kind: "batch";
  readonly batchUuid: Uuid;
};

export type ArtefactRef = {
  readonly kind: "artefact";
  readonly artefactUuid: Uuid;
};

export type ArtefactBySlugRef = {
  readonly kind: "artefactBySlug";
  readonly handle: Handle;
  readonly slug: Slug;
};

export type CollectionRef = {
  readonly kind: "collection";
  readonly collectionUuid: Uuid;
};

export type CollectionBySlugRef = {
  readonly kind: "collectionBySlug";
  readonly handle: Handle;
  readonly slug: Slug;
};

/** Discriminated union of every URL-addressable entity reference. */
export type EntityRef =
  | RecipeRef
  | RecipeBySlugRef
  | AttemptRef
  | BranchRef
  | RecipeBatchRef
  | BatchRef
  | ArtefactRef
  | ArtefactBySlugRef
  | CollectionRef
  | CollectionBySlugRef;

export type EntityKind = EntityRef["kind"];

// ---------------------------------------------------------------------------
// Validation regexes — exported for reuse by sibling modules (not the public
// package surface). Kept as constants so the parser and builder agree.
// ---------------------------------------------------------------------------

/** UUID canonical form: 8-4-4-4-12 lowercase hex.
 *  Intentionally permissive on version bits — the UUIDv7 generator is a
 *  separate sub-task; the resolver only checks shape. */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Handle / slug: lowercase alphanumerics and hyphens, 1-64 characters,
 *  must start with an alphanumeric. Avoids leading hyphens and keeps
 *  URLs readable. The trailing-hyphen and `--` cases are allowed by
 *  the regex but should be avoided by slug generation — the resolver
 *  permits them on input because it has to accept whatever the
 *  slug-history table legitimately stored. */
export const HANDLE_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function isUuid(v: string): boolean {
  return UUID_RE.test(v);
}

export function isHandleOrSlug(v: string): boolean {
  return HANDLE_SLUG_RE.test(v);
}
